import { ZyNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { ImageEditDialog } from '@/components/tiptap-editor/tiptap-extension/image-edit-dialog';
import { ImageEditTooltip } from '@/components/tiptap-editor/tiptap-extension/image-edit-tooltip';
import { ImageHoverAltText } from '@/components/tiptap-editor/tiptap-extension/image-hover-alttext';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import type { ReactNodeViewProps } from '@tiptap/react';
import { memo, useState } from 'react';

import {
    convertImageNodeType,
    normalizeImportedImageSrc,
    useImageDragSurface,
    useImageResizableMount,
} from '@/components/tiptap-editor/tiptap-extension/image-utils';
import { syncMaImageAltText } from '@/lib/ma-image-alt-text';
import { MAX_IMAGE_WIDTH, MIN_IMAGE_WIDTH } from '@/lib/utils';

function ZyInlineImageResizableMount({
    containerRef,
    editor,
    getPos,
}: {
    containerRef: React.RefObject<HTMLElement | null>;
    editor: ReactNodeViewProps['editor'];
    getPos: ReactNodeViewProps['getPos'];
}) {
    useImageDragSurface({ containerRef, editor, getPos, nodeTypeName: 'zyInlineImage' });

    return (
        <div
            ref={containerRef as any}
            className="block w-fit max-w-full"
        />
    );
}

const ZyInlineImageNodeView: React.FC<ReactNodeViewProps> = ({
    node,
    selected,
    updateAttributes,
    editor,
    getPos,
    decorations,
    innerDecorations,
}) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { attrs } = node;
    const normalizedSrc = normalizeImportedImageSrc(attrs, editor.state?.doc?.attrs);
    const { containerRef, isResizing } = useImageResizableMount({
        editor,
        node,
        getPos,
        nodeTypeName: 'zyInlineImage',
        decorations,
        innerDecorations,
    });
    const [isHovering, setIsHovering] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [alt, setAlt] = useState(attrs.alt || '');
    const [width, setWidth] = useState(attrs.width || 120);
    const [height, setHeight] = useState(attrs.height || Math.round((attrs.width || 120) / (attrs.aspectRatio || 1)));
    const [pull, setPull] = useState(attrs.pull || null);
    const [displayType, setDisplayType] = useState<'block' | 'inline'>('inline');

    const aspectRatio = attrs.aspectRatio ?? 1;
    const hasAlt = !!attrs.alt;

    const refreshImageProperties = () => {
        setAlt(attrs.alt || '');
        setWidth(attrs.width || 120);
        setHeight(attrs.height || Math.round((attrs.width || 120) / aspectRatio));
        setPull(attrs.pull || null);
        setDisplayType('inline');
    };

    const saveImage = () => {
        const trimmedAlt = alt?.trim() ?? '';
        const normalizedWidth = Math.max(MIN_IMAGE_WIDTH, Math.min(MAX_IMAGE_WIDTH, width));
        const normalizedHeight = Math.max(MIN_IMAGE_WIDTH, Math.min(MAX_IMAGE_WIDTH, height));
        const sharedAttrs = {
            ...attrs,
            alt: trimmedAlt,
            width: normalizedWidth,
            height: normalizedHeight,
            aspectRatio: normalizedWidth / normalizedHeight,
        };

        if (displayType === 'block') {
            convertImageNodeType(editor, getPos, 'block', { ...sharedAttrs, pull });
            syncMaImageAltText(attrs.maImageId, trimmedAlt);
            setIsOpen(false);
            return;
        }

        updateAttributes({ ...sharedAttrs, pull: null });
        syncMaImageAltText(attrs.maImageId, trimmedAlt);
        setIsOpen(false);
    };

    return (
        <>
            <ZyNodeViewWrapper
                decorations={decorations}
                as="span"
                label="Inline Image"
                selected={selected}
                data-inline-image="true"
                className="leading-[0]"
            >
                <div
                    className="relative block w-fit max-w-full"
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    onDoubleClick={() => {
                        if (isEditorReadonly) {
                            return;
                        }
                        // refreshImageProperties is required so the modal can contain the current
                        // height and width of the photo. On first render, attrs.height/width are null,
                        // and this func allows us to retrieve them once they are loaded.
                        refreshImageProperties();
                        setIsOpen(true);
                    }}
                >
                    <ZyInlineImageResizableMount
                        containerRef={containerRef}
                        editor={editor}
                        getPos={getPos}
                    />

                    <ImageEditTooltip
                        isHovering={isHovering}
                        isResizing={isResizing}
                        hasAlt={hasAlt}
                        width={attrs.width}
                        refreshImageProperties={refreshImageProperties}
                        setIsOpen={setIsOpen}
                        editDisabled={isEditorReadonly}
                    />

                    <ImageHoverAltText
                        isResizing={isResizing}
                        isHovering={isHovering}
                        alt={attrs.alt}
                    />
                </div>
            </ZyNodeViewWrapper>
            <ImageEditDialog
                open={isOpen}
                onOpenChange={open => {
                    if (open && isEditorReadonly) {
                        return;
                    }
                    if (open) {
                        refreshImageProperties();
                    }
                    setIsOpen(open);
                }}
                maImageId={attrs.maImageId}
                currentMaDocumentId={editor.state.doc.attrs.documentId}
                previewSrc={normalizedSrc}
                previewAlt={attrs.alt}
                previewWidth={attrs.width}
                previewHeight={attrs.height}
                alt={alt}
                setAlt={setAlt}
                aspectRatio={aspectRatio}
                height={height}
                setHeight={setHeight}
                width={width}
                setWidth={setWidth}
                displayType={displayType}
                onDisplayTypeChange={setDisplayType}
                pull={pull}
                onPullChange={setPull}
                onCancel={() => setIsOpen(false)}
                onSave={saveImage}
            />
        </>
    );
};

export default memo(ZyInlineImageNodeView);
